import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, KpiCard, Card, Spinner, Select } from "@/components/common/UI";
import { RegionFilters } from "@/components/common/Filters";
import { StatusBadge, StockBadge } from "@/components/common/Badges";
import { money, num, fmtDate, REGION_FLAG } from "@/lib/format";
import { Banknote, ShoppingBag, Boxes, Clock, AlertTriangle } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar } from "recharts";

export default function DashboardPage() {
  const { user, currency } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ region: "all", country: "all", dc_id: "all", date_range: "all" });
  const [d, setD] = useState(null);

  useEffect(() => {
    setD(null);
    api.get("/dashboard", { params: { ...f, currency } }).then((r) => setD(r.data));
  }, [f, currency]);

  const greeting = new Date().getHours() < 12 ? "Good morning" : new Date().getHours() < 18 ? "Good afternoon" : "Good evening";

  return (
    <div data-testid="dashboard-page">
      <PageHeader title={`${greeting}, ${user.role === "admin" ? user.name : user.name.split(" ")[0]}`} subtitle={user.title || "Overview of orders, stock and forecasts across your scope"} />
      <RegionFilters value={f} onChange={setF} extra={
        <Select label="Date Range" testId="filter-date-range" value={f.date_range} onChange={(v) => setF({ ...f, date_range: v })}
          options={[{ value: "7d", label: "Last 7 days" }, { value: "30d", label: "Last 30 days" }, { value: "3m", label: "Last 3 months" }, { value: "6m", label: "Last 6 months" }, { value: "all", label: "All time" }]} />} />
      {!d ? <Spinner /> : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
            <KpiCard testId="kpi-revenue" label="Total Revenue" value={money(d.total_revenue, d.currency, 0)} hint={`Shown in ${d.currency}`} icon={Banknote} tone="red" delay={0} />
            <KpiCard testId="kpi-aov" label="Avg Order Value" value={money(d.avg_order_value, d.currency)} icon={ShoppingBag} tone="slate" delay={60} />
            <KpiCard testId="kpi-units" label="Units Ordered" value={num(d.total_units)} hint="bags" icon={Boxes} tone="indigo" delay={120} />
            <KpiCard testId="kpi-pending" label="Pending Approval" value={num(d.pending_orders)} hint={`${num(d.total_orders)} total orders`} icon={Clock} tone="amber" delay={180} onClick={() => nav("/approvals")} />
            <KpiCard testId="kpi-low-stock" label="Stock Alerts" value={num(d.low_stock_count)} hint="SKUs below threshold" icon={AlertTriangle} tone={d.low_stock_count ? "red" : "emerald"} delay={240} onClick={() => nav("/warehouse")} />
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-3">
            <Card className="p-5 lg:col-span-2" data-testid="revenue-trend-chart">
              <div className="mb-4 flex items-center justify-between"><h3 className="font-display text-lg font-bold">Order volume trend</h3><span className="font-mono text-xs text-slate-400">{d.currency} · monthly</span></div>
              <div className="h-64">
                <ResponsiveContainer>
                  <AreaChart data={d.trend} margin={{ left: 0, right: 8, top: 8 }}>
                    <defs><linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#DC2626" stopOpacity={0.35} /><stop offset="100%" stopColor="#DC2626" stopOpacity={0} /></linearGradient></defs>
                    <CartesianGrid vertical={false} stroke="#EEF0F3" />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} width={48} />
                    <Tooltip formatter={(v, n) => (n === "revenue" ? money(v, d.currency) : num(v))} contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }} />
                    <Area type="monotone" dataKey="revenue" stroke="#DC2626" strokeWidth={2} fill="url(#rev)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Card>
            <Card className="p-5" data-testid="forecast-card">
              <h3 className="font-display text-lg font-bold">Forecast vs demand</h3>
              <p className="text-xs text-slate-500">Forecasted units vs actual ordered</p>
              <div className="mt-5 space-y-4">
                {[["Forecasted", d.forecast.forecasted, "bg-slate-300"], ["Actual", d.forecast.actual, "bg-red-600"]].map(([l, v, c]) => (
                  <div key={l}><div className="flex justify-between text-sm"><span className="text-slate-500">{l}</span><span className="font-semibold tabular-nums">{num(v)}</span></div>
                    <div className="mt-1.5 h-2 rounded-full bg-slate-100"><div className={`h-2 rounded-full ${c} transition-all duration-700`} style={{ width: `${Math.min(100, (v / Math.max(d.forecast.forecasted, d.forecast.actual, 1)) * 100)}%` }} /></div></div>
                ))}
              </div>
              <div className="mt-6 rounded-xl bg-slate-900 p-4 text-white"><p className="text-xs uppercase tracking-wider text-slate-400">Forecast attainment</p><p className="font-display text-3xl font-extrabold" data-testid="forecast-accuracy">{d.forecast.accuracy}%</p></div>
            </Card>
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-3">
            <Card className="p-5 lg:col-span-2">
              <div className="mb-4 flex items-center justify-between"><h3 className="font-display text-lg font-bold">Recent orders</h3><button data-testid="view-all-orders" onClick={() => nav("/history")} className="text-sm font-semibold text-red-600 hover:underline">View all</button></div>
              <div className="divide-y divide-slate-100" data-testid="recent-orders-list">
                {d.recent_orders.length === 0 && <p className="py-8 text-center text-sm text-slate-400">No orders in this range</p>}
                {d.recent_orders.map((o) => (
                  <button key={o.id} onClick={() => nav(`/orders/${o.id}`)} className="flex w-full items-center gap-4 py-3 text-left transition-colors hover:bg-slate-50 -mx-2 px-2 rounded-lg">
                    <span className="text-lg">{REGION_FLAG[o.region]}</span>
                    <span className="min-w-0 flex-1"><span className="block truncate text-sm font-semibold text-slate-900">{o.store_name}</span><span className="text-xs text-slate-500 font-mono">{o.order_number} · {fmtDate(o.created_at)}</span></span>
                    <span className="text-sm font-semibold tabular-nums">{money(o.total, o.currency)}</span>
                    <StatusBadge status={o.status} />
                  </button>
                ))}
              </div>
            </Card>
            <div className="space-y-6">
              <Card className="p-5">
                <h3 className="font-display text-lg font-bold">By region</h3>
                <div className="mt-3 h-40">
                  <ResponsiveContainer><BarChart data={d.by_region} margin={{ left: -20, top: 4 }}><XAxis dataKey="region" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} /><YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} /><Tooltip formatter={(v) => num(v)} contentStyle={{ borderRadius: 12, fontSize: 12 }} /><Bar dataKey="units" fill="#0F172A" radius={[6, 6, 0, 0]} /></BarChart></ResponsiveContainer>
                </div>
              </Card>
              <Card className="p-5" data-testid="low-stock-card">
                <h3 className="font-display text-lg font-bold">Stock alerts</h3>
                <div className="mt-3 space-y-2.5">
                  {d.low_stock.map((p) => (
                    <div key={p.id} className="flex items-center justify-between gap-2 text-sm"><span className="min-w-0"><span className="block truncate font-medium">{p.name}</span><span className="font-mono text-xs text-slate-400">{p.sku} · net {num(p.net_available)}</span></span><StockBadge status={p.status} /></div>
                  ))}
                  {d.low_stock.length === 0 && <p className="text-sm text-slate-400">All SKUs healthy</p>}
                </div>
              </Card>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
